const GSM7_BASIC_CHARS = new Set(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
    "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà"
);

const GSM7_EXTENDED_CHARS = new Set("^{}\\[~]|€");

const GSM7_SINGLE_SEGMENT_LIMIT = 160;
const GSM7_CONCAT_SEGMENT_LIMIT = 153;
const UCS2_SINGLE_SEGMENT_LIMIT = 70;
const UCS2_CONCAT_SEGMENT_LIMIT = 67;

export const MAX_SMS_SEGMENTS = 10;

const TOKEN_PATTERN = /[^ \t\r\n\f\v]+|[ \t\r\n\f\v]+/g;
const LEADING_WHITESPACE = /^[ \t\r\n\f\v]/;

type SmsEncoding = "gsm7" | "ucs2";

interface SmsBreakdown {
  encoding: SmsEncoding;
  totalLength: number;
  segmentCount: number;
  segmentLengths: number[];
  overLimit: boolean;
}

const encodingFor = (text: string): SmsEncoding =>
  [...text].every(
    (char) => GSM7_BASIC_CHARS.has(char) || GSM7_EXTENDED_CHARS.has(char)
  )
    ? "gsm7"
    : "ucs2";

const lengthIn = (encoding: SmsEncoding, text: string): number => {
  const chars = [...text];
  if (encoding === "ucs2")
    return chars.reduce((sum, char) => sum + char.length, 0);
  return chars.reduce(
    (sum, char) => sum + (GSM7_EXTENDED_CHARS.has(char) ? 2 : 1),
    0
  );
};

const packSegments = (
  encoding: SmsEncoding,
  text: string,
  capacity: number
): number[] => {
  const lengths = [0];
  const tokens = text.match(TOKEN_PATTERN) ?? [];

  tokens.forEach((token) => {
    let tokenLength = lengthIn(encoding, token);
    if (LEADING_WHITESPACE.test(token) || tokenLength > capacity)
      while (tokenLength > 0) {
        if (lengths[lengths.length - 1] === capacity) lengths.push(0);
        const last = lengths.length - 1;
        const take = Math.min(tokenLength, capacity - lengths[last]);
        lengths[last] += take;
        tokenLength -= take;
      }
    else {
      const last = lengths[lengths.length - 1];
      if (last + tokenLength > capacity) lengths.push(0);
      lengths[lengths.length - 1] += tokenLength;
    }
  });

  return lengths;
};

export const getSmsBreakdown = (text: string): SmsBreakdown => {
  const encoding = encodingFor(text);
  const totalLength = lengthIn(encoding, text);
  const [single, concat] =
    encoding === "gsm7"
      ? [GSM7_SINGLE_SEGMENT_LIMIT, GSM7_CONCAT_SEGMENT_LIMIT]
      : [UCS2_SINGLE_SEGMENT_LIMIT, UCS2_CONCAT_SEGMENT_LIMIT];

  let segmentLengths: number[] = [];
  if (totalLength > 0)
    segmentLengths =
      totalLength <= single
        ? [totalLength]
        : packSegments(encoding, text, concat);

  return {
    encoding,
    totalLength,
    segmentCount: segmentLengths.length,
    segmentLengths,
    overLimit: segmentLengths.length > MAX_SMS_SEGMENTS
  };
};
